import React from 'react';
import { connect } from 'react-redux';
import { Button, InternalLink } from 'ctm-components';
import classNames from 'classnames';
import { pageContext } from 'ctm-util-functions';
import { splitTrafficIdSelector } from 'ctm-selectors';
import { useStyles } from './car_series_styles';

/**
 * Render the Car Details component
 * @param {Object} props - the incoming set of props
 * @returns {Node} A component
 */

const _CarSeries = ({ series, updateBadge, updateSeries, currentSeries, finaliseManualLookup, resetValues, resetTouches, setFormValue, elementTouched, splitTrafficId }) => {
    const seriesResponse = JSON.parse(JSON.stringify(series));

    const dispatchEvents = (redbookCode, badge) => {
        setFormValue(redbookCode);
        if (badge) {
            updateBadge(badge);
        }
        updateSeries(redbookCode);
        elementTouched(redbookCode);
        finaliseManualLookup(redbookCode);
        resetValues(redbookCode);
        resetTouches(redbookCode);
    };

    // Sort an array of objects by the "RELEASE" description (which contains month names)
    const sortSeriesResponseByReleaseDate = seriesResponse.sort((a, b) => {
        // Define an object that maps month names to numeric values for sorting.
        const monthOrder = {
            January: 1,
            February: 2,
            March: 3,
            April: 4,
            May: 5,
            June: 6,
            July: 7,
            August: 8,
            September: 9,
            October: 10,
            November: 11,
            December: 12
        };

        // Extract the first word from the "RELEASE" description for objects 'a' and 'b'.
        const aMonth = a?.descriptions?.RELEASE?.description?.split(' ')[0];
        const bMonth = b?.descriptions?.RELEASE?.description?.split(' ')[0];

        if (monthOrder[aMonth] < monthOrder[bMonth]) {
            return -1;
        }
        if (monthOrder[aMonth] > monthOrder[bMonth]) {
            return 1;
        }
        return 0;
    });

    // If passed in field is null, hide it
    const updateClassName = (field) => {
        let cssClass = '';
        if (field === null || field === 'NO_BADGE') {
            cssClass = classes.hide;
        }

        return cssClass;
    };

    const isXS = pageContext.getProps().pageState.breakpoint === 'xs';

    const classes = useStyles({ isXS });

    const seriesBVariant = () => {
        return sortSeriesResponseByReleaseDate.map((carSeries) => {
            return (
                <InternalLink
                    className={classes.unset}
                    href="/car-insurance/journey/car_colour"
                    key={carSeries.redbookCode}
                    data-id={`internal-link-${carSeries.redbookCode}`}
                >
                    <Button
                        className={classNames(classes.buttonContainer, currentSeries === carSeries.redbookCode ? classes.active : '')}
                        data-id={`button-${carSeries.redbookCode}`}
                        onClick={() => {
                            dispatchEvents(carSeries.redbookCode, carSeries.badge.code);
                        }}
                    >
                        {isXS ? (
                            <div className={classNames(classes.row, classes.spacing)}>
                                <div className={classes.fixedWidth}>
                                    <span className={classNames(classes.row, updateClassName(carSeries.descriptions.SERIES.description), classes.xsMarginBottom, classes.wrap)}>
                                        <p className={classNames(classes.title, classes.justifyText)}>
                                            {carSeries.descriptions.SERIES.label}
                                            :
                                            <span className={classNames(classes.description, classes.title, currentSeries === carSeries.redbookCode ? classes.active : '')}>
                                                {' '}
                                                {carSeries.descriptions.SERIES.description}
                                            </span>
                                        </p>
                                    </span>
                                </div>
                                <div className={classes.rightColumnWidth}>
                                    <span className={classNames(classes.row, updateClassName(carSeries.descriptions.BADGE.description), classes.xsMarginBottom, classes.wrap)}>
                                        <p className={classNames(classes.title, classes.justifyText)}>
                                            {carSeries.descriptions.BADGE.label}
                                            :
                                            <span className={classNames(classes.description, classes.title, currentSeries === carSeries.redbookCode ? classes.active : '')}>
                                                {' '}
                                                {carSeries.descriptions.BADGE.description}
                                            </span>
                                        </p>
                                    </span>
                                </div>
                                <div className={classNames(classes.column, classes.fixedWidth)}>
                                    <div className={classNames(classes.column, classes.spacing, classes.subtext, classes.wrap)}>
                                        <div className={classNames(classes.row, updateClassName(carSeries.descriptions.ENGINE.description, classes.wrap))}>
                                            <p className={classNames(classes.label, classes.justifyText)}>
                                                {carSeries.descriptions.ENGINE.label}
                                                :
                                                <span className={classNames(classes.description, currentSeries === carSeries.redbookCode ? classes.active : '')}>
                                                    {' '}
                                                    {carSeries.descriptions.ENGINE.description}
                                                </span>
                                            </p>
                                        </div>
                                        <div className={classNames(classes.row, updateClassName(carSeries.descriptions.POWER.description))}>
                                            <p className={classes.label}>
                                                {carSeries.descriptions.POWER.label}
                                                :
                                                <span className={classNames(classes.description, currentSeries === carSeries.redbookCode ? classes.active : '')}>
                                                    {' '}
                                                    {carSeries.descriptions.POWER.description}
                                                </span>
                                            </p>
                                        </div>
                                        <div className={classNames(classes.row, updateClassName(carSeries.descriptions.KERBMASS.description))}>
                                            <p className={classNames(classes.label, classes.justifyText)}>
                                                {carSeries.descriptions.KERBMASS.label}
                                                :
                                                <span className={classNames(classes.description, currentSeries === carSeries.redbookCode ? classes.active : '')}>
                                                    {' '}
                                                    {carSeries.descriptions.KERBMASS.description}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className={classNames(classes.column, classes.rightColumnWidth)}>
                                    <div className={classNames(classes.column, classes.spacing, classes.subtext)}>
                                        <div className={classNames(classes.row, updateClassName(carSeries.descriptions.TRANSMISSION.description, classes.wrap))}>
                                            <p className={classNames(classes.label, classes.justifyText)}>
                                                {carSeries.descriptions.TRANSMISSION.label}
                                                :
                                                <span className={classNames(classes.description, currentSeries === carSeries.redbookCode ? classes.active : '')}>
                                                    {' '}
                                                    {carSeries.descriptions.TRANSMISSION.description}
                                                </span>
                                            </p>
                                        </div>

                                        <div className={classNames(classes.row, updateClassName(carSeries.descriptions.DRIVE.description))}>
                                            <p className={classNames(classes.label, classes.justifyText)}>
                                                {carSeries.descriptions.DRIVE.label}
                                                :
                                                <span className={classNames(classes.description, currentSeries === carSeries.redbookCode ? classes.active : '')}>
                                                    {' '}
                                                    {carSeries.descriptions.DRIVE.description}
                                                </span>
                                            </p>
                                        </div>

                                        <div className={classNames(classes.row, updateClassName(carSeries.descriptions.RELEASE.description))}>
                                            <p className={classes.label}>
                                                {carSeries.descriptions.RELEASE.label}
                                                :
                                            </p>
                                            <p className={classes.description}>{carSeries.descriptions.RELEASE.description}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className={classes.row}>
                                    <span className={classNames(classes.row, updateClassName(carSeries.descriptions.SERIES.description))}>
                                        <p className={classes.title}>
                                            {carSeries.descriptions.SERIES.label}
                                            :
                                        </p>
                                        <p className={classNames(classes.description, classes.seriesFont)}>{carSeries.descriptions.SERIES.description}</p>
                                    </span>
                                    <span className={classNames(classes.row, updateClassName(carSeries.descriptions.BADGE.description))}>
                                        <span className={classNames(classes.divider, updateClassName(carSeries.descriptions.SERIES.description))}>|</span>
                                        <p className={classes.title}>
                                            {carSeries.descriptions.BADGE.label}
                                            :
                                        </p>
                                        <p className={classNames(classes.description, classes.title)}>{carSeries.descriptions.BADGE.description}</p>
                                    </span>
                                </div>
                                <div className={classNames(classes.row, classes.wrap, classes.subtext)}>
                                    <div className={classNames(classes.row, classes.contentWrapper, updateClassName(carSeries.descriptions.ENGINE.description))}>
                                        <p className={classes.label}>
                                            {carSeries.descriptions.ENGINE.label}
                                            :
                                            {' '}
                                        </p>
                                        <p className={classes.description}>
                                            {carSeries.descriptions.ENGINE.description}
                                            {' '}
                                        </p>
                                        <span className={classes.divider}>|</span>
                                    </div>
                                    <div className={classNames(classes.row, classes.contentWrapper, updateClassName(carSeries.descriptions.TRANSMISSION.description))}>
                                        <p className={classes.label}>
                                            {carSeries.descriptions.TRANSMISSION.label}
                                            :
                                        </p>
                                        <p className={classes.description}>
                                            {carSeries.descriptions.TRANSMISSION.description}
                                            {' '}
                                        </p>
                                        <span className={classes.divider}>|</span>
                                    </div>
                                    <div className={classNames(classes.row, classes.contentWrapper, updateClassName(carSeries.descriptions.POWER.description))}>
                                        <p className={classes.label}>
                                            {carSeries.descriptions.POWER.label}
                                            :
                                        </p>
                                        <p className={classes.description}>
                                            {carSeries.descriptions.POWER.description}
                                            {' '}
                                        </p>
                                        <span className={classes.divider}>|</span>
                                    </div>
                                    <div className={classNames(classes.row, classes.contentWrapper, updateClassName(carSeries.descriptions.DRIVE.description))}>
                                        <p className={classes.label}>
                                            {carSeries.descriptions.DRIVE.label}
                                            :
                                        </p>
                                        <p className={classes.description}>
                                            {carSeries.descriptions.DRIVE.description}
                                            {' '}
                                        </p>
                                        <span className={classes.divider}>|</span>
                                    </div>
                                    <div className={classNames(classes.row, classes.contentWrapper, updateClassName(carSeries.descriptions.KERBMASS.description))}>
                                        <p className={classes.label}>
                                            {carSeries.descriptions.KERBMASS.label}
                                            :
                                        </p>
                                        <p className={classes.description}>
                                            {carSeries.descriptions.KERBMASS.description}
                                            {' '}
                                        </p>
                                        <span className={classes.divider}>|</span>
                                    </div>
                                    <div className={classNames(classes.row, classes.contentWrapper, updateClassName(carSeries.descriptions.RELEASE.description))}>
                                        <p className={classes.label}>
                                            {carSeries.descriptions.RELEASE.label}
                                            :
                                        </p>
                                        <p className={classes.description}>{carSeries.descriptions.RELEASE.description}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Button>
                </InternalLink>
            );
        });
    };
    return <div className={classes.mainContainer}>{seriesBVariant()}</div>;
};

const mapDispatchToProps = (dispatch) => {
    return {
        updateBadge: (badgeCode) => dispatch({
            type: 'SET_VALUE',
            value: badgeCode,
            identifier: 'coverDetail.vehicle.badge',
            stateKey: 'vertical.quote'
        }),
        updateSeries: (series) => dispatch({
            type: 'SET_VALUE',
            value: series,
            identifier: 'coverDetail.vehicle.redbookCode',
            stateKey: 'vertical.quote'
        }),
        finaliseManualLookup: (redbookCode) => dispatch({ type: 'FINALISE_MANUAL_LOOKUP', value: redbookCode, stateKey: 'vertical.quote' }),
        resetValues: (redbookCode) => dispatch({
            type: 'RESET_VALUES',
            identifiers: [
                'coverDetail.vehicle.colour',
                'coverDetail.vehicle.securityFeatures',
                'coverDetail.vehicle.factoryOptions',
                'coverDetail.vehicle.nonStandardAccessories'
            ],
            value: redbookCode,
            stateKey: 'vertical.quote'
        }),
        resetTouches: (redbookCode) => dispatch({
            type: 'RESET_TOUCHES',
            identifiers: ['coverDetail.vehicle.colour'],
            value: redbookCode,
            stateKey: 'vertical.quote'
        }),
        setFormValue: (redbookCode) => dispatch({
            type: 'SET_FORM_VALUE',
            value: redbookCode,
            identifier: 'coverDetail.vehicle.redbookCode',
            stateKey: 'vertical.quote'
        }),
        elementTouched: (redbookCode) => dispatch({
            type: 'ELEMENT_TOUCHED',
            identifier: 'coverDetail.vehicle.redbookCode',
            value: redbookCode,
            stateKey: 'vertical.quote'
        })
    };
};

/**
 * Map state to our component props
 * @param {Object} state - the application state
 * @returns {Object} - the set of mapped props for the component
 */
const mapStateToProps = (state) => {
    const series = state.getIn(['vertical', 'quote', 'apiResponses', 'series']) || [];
    const currentSeries = state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'vehicle', 'redbookCode']);
    const splitTrafficId = splitTrafficIdSelector(state);

    return {
        series,
        currentSeries,
        splitTrafficId
    };
};

const CarSeries = connect(
    mapStateToProps,
    mapDispatchToProps
)(_CarSeries);

export { CarSeries, _CarSeries };
